/**
 * Typed fetch client for the portal half of the API contract.
 *
 * Paths are built from API_ROUTES, never written out by hand, so a path that
 * moves in the contract moves here with it. Request and response shapes come
 * from ApiContract. Nothing in this file describes a payload the contract has
 * already described.
 *
 * The session is an HttpOnly cookie, so there is no token handling here and
 * nothing to keep in memory. Every call is same origin and sends the cookie.
 */

import {
  API_ERROR_CODE,
  API_ROUTES,
  type AcceptNdaResponse,
  type ApiErrorBody,
  type ApiErrorCode,
  type ApiRouteKey,
  type CreateDocumentUrlResponse,
  type ListPortalAccessResponse,
  type ListPortalDocumentsResponse,
  type RequestOtpResponse,
  type RevealCredentialResponse,
  type RouteBody,
  type RouteDefinition,
  type RouteParams,
  type RouteResponse,
  type VerifyOtpResponse,
} from '@insuredhq/contract';

/**
 * A failed call, carrying the contract's error code so callers branch on the
 * code rather than on a message. Messages from the API are deliberately dull
 * and are never shown to the customer; the portal picks its own wording.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;

  constructor(code: ApiErrorCode, status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }

  /** True when the customer needs to authenticate again from the email gate. */
  get isSessionOver(): boolean {
    return (
      this.code === API_ERROR_CODE.UNAUTHENTICATED ||
      this.code === API_ERROR_CODE.FORBIDDEN ||
      this.code === API_ERROR_CODE.NOT_FOUND
    );
  }

  get isNdaRequired(): boolean {
    return this.code === API_ERROR_CODE.NDA_REQUIRED;
  }
}

const PARAM_PATTERN = /:([A-Za-z0-9_]+)/g;
type CallableApiRouteKey = Exclude<ApiRouteKey, 'portal.getDocumentContent'>;

function buildPath<K extends CallableApiRouteKey>(key: K, params: RouteParams<K>): string {
  const route: RouteDefinition = API_ROUTES[key];
  const values = params as Record<string, string>;
  return route.path.replace(PARAM_PATTERN, (_match, name: string) => {
    const value = values[name];
    if (typeof value !== 'string' || value.length === 0) {
      throw new ApiError(API_ERROR_CODE.BAD_REQUEST, 0, `Missing path parameter ${name}.`);
    }
    return encodeURIComponent(value);
  });
}

async function readError(response: Response): Promise<ApiError> {
  let code: ApiErrorCode = API_ERROR_CODE.INTERNAL;
  let message = 'The request could not be completed.';
  try {
    const body = (await response.json()) as Partial<ApiErrorBody>;
    if (body.error) {
      code = body.error.code;
      message = body.error.message;
    }
  } catch {
    /* A response with no JSON body is still an error; keep the defaults. */
  }
  return new ApiError(code, response.status, message);
}

async function call<K extends CallableApiRouteKey>(
  key: K,
  params: RouteParams<K>,
  body?: RouteBody<K>,
): Promise<RouteResponse<K>> {
  const route: RouteDefinition = API_ROUTES[key];
  const init: RequestInit = {
    method: route.method,
    credentials: 'same-origin',
    redirect: 'follow',
    /* Nothing this client asks for is worth keeping, and one answer carries a
     * secret. The API sends no-store as well; this says it from our side. */
    cache: 'no-store',
    headers: { accept: 'application/json' },
  };

  if (body !== undefined) {
    init.headers = { ...init.headers, 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(buildPath(key, params), init);
  } catch (cause) {
    throw new ApiError(
      API_ERROR_CODE.INTERNAL,
      0,
      cause instanceof Error ? cause.message : 'Network request failed.',
    );
  }

  if (!response.ok) {
    throw await readError(response);
  }

  return (await response.json()) as RouteResponse<K>;
}

/* -------------------------------------------------------------------------- */
/* Portal calls                                                               */
/* -------------------------------------------------------------------------- */

export function requestOtp(
  slug: string,
  email: string,
  turnstileToken: string,
): Promise<RequestOtpResponse> {
  return call('portal.requestOtp', { slug }, { email, turnstileToken });
}

export function verifyOtp(
  slug: string,
  email: string,
  code: string,
  turnstileToken: string,
): Promise<VerifyOtpResponse> {
  return call('portal.verifyOtp', { slug }, { email, code, turnstileToken });
}

export function listDocuments(slug: string): Promise<ListPortalDocumentsResponse> {
  return call('portal.listDocuments', { slug });
}

export function acceptNda(slug: string, ndaVersion: string): Promise<AcceptNdaResponse> {
  return call('portal.acceptNda', { slug }, { ndaVersion });
}

/**
 * Authorises one document and returns where to send the browser.
 *
 * The URL is opaque to this application. It is not parsed, stored, cached or
 * reconstructed, and nothing here decides how the bytes will arrive.
 */
export function createDocumentUrl(
  slug: string,
  documentId: string,
): Promise<CreateDocumentUrlResponse> {
  return call('portal.createDocumentUrl', { slug, documentId }, {});
}

/** Links for the customer and the signed-in address's own credentials. No secrets. */
export function listAccess(slug: string): Promise<ListPortalAccessResponse> {
  return call('portal.listAccess', { slug });
}

/**
 * Reveals one credential's secret. The server answers this once, ever, for a
 * given credential, so the caller holds the plaintext in memory and nowhere
 * else. POST only: the path answers GET with 405 so that nothing which merely
 * follows a link can spend the reveal.
 */
export function revealCredential(
  slug: string,
  credentialId: string,
): Promise<RevealCredentialResponse> {
  return call('portal.revealCredential', { slug, credentialId }, {});
}
